import { BigNumber, Overrides, utils } from "ethers";
import { ContractTransaction } from "@ethersproject/contracts";

type TxOverrideArgs = {
  gasPrice: number;
  gasLimit?: number;
};

export function formatBigNumber(
  value: number | string,
  units: number
): BigNumber {
  return utils.parseUnits(value.toString(), units);
}

export function createTxOptions(args: TxOverrideArgs): Overrides {
  const { gasLimit } = args;
  const gasPrice = formatBigNumber(args.gasPrice, 9).toString();
  if (gasLimit) {
    return { gasPrice, gasLimit };
  } else {
    return { gasPrice };
  }
}

const eip1559Error = "max fee per gas less than block base fee";
function isErrorGasRelated(error: any) {
  return (
    error.code == "UNPREDICTABLE_GAS_LIMIT" ||
    (error.message && error.message.includes(eip1559Error))
  );
}

type GasPriceOptions = {
  gasPriceSetting?: "low" | "medium" | "high";
  defaultGasPrice?: number;
};

export async function getGasPriceEstimate(options: GasPriceOptions = {}) {
  const { gasPriceSetting = "high", defaultGasPrice = 65 } = options;
  try {
    let gasPrice;
    const etherchainResponse = await (
      await fetch(
        "https://gas-api.metaswap.codefi.network/networks/1/suggestedGasFees"
      )
    ).json();
    if (etherchainResponse.estimatedBaseFee) {
      // With 1559 we just want to use the base fee
      gasPrice = Number(etherchainResponse.estimatedBaseFee);
    } else {
      gasPrice = Number(
        etherchainResponse[gasPriceSetting].suggestedMaxFeePerGas
      );
    }
    return !isNaN(Number(gasPrice)) ? Number(gasPrice) : defaultGasPrice;
  } catch (error) {
    console.log(error);
    console.log(`Using ${defaultGasPrice} gwei as default`);
    return defaultGasPrice;
  }
}

export async function executeTransactionWithRetry(
  txOverrides: TxOverrideArgs,
  tx: (txOptions: Overrides) => Promise<ContractTransaction>
) {
  const { gasPrice, gasLimit } = txOverrides;
  try {
    const options = createTxOptions({ gasPrice });
    return await tx(options);
  } catch (error) {
    if (gasLimit && isErrorGasRelated(error)) {
      // Retry with an explicit gas limit
      console.log(`Retry tx with explicit gas limit: ${gasLimit}`);
      const options = createTxOptions({ gasPrice, gasLimit });
      return tx(options);
    } else {
      throw error;
    }
  }
}
